import { Stack, Typography } from '@mui/material';

import { CheckCircleBrokenIcon } from '@/components/icons/CheckCircleBrokenIcon';
import { LinkExternal01Icon } from '@/components/icons/LinkExternal01Icon';
import { MagicWand01Icon } from '@/components/icons/MagicWand01Icon';
import { ZapFastIcon } from '@/components/icons/ZapFastIcon';
import { COLORS } from '@/styles/tokens/colors';
import { FONT_WEIGHTS } from '@/styles/tokens/fonts';

import {
  OnboardingValueProp,
  OnboardingValuePropCards,
} from '../../components/OnboardingValuePropCards';
import { EnableSmartReviewCard } from './components/EnableSmartReviewCard';
import { useSmartReviewStepContext } from './context/smartReviewStep.context';

// Content sits 140px from the modal edge (Figma); the wizard's body wrapper
// adds 24px, so add the remaining 116px here.
const CONTENT_INSET_PX = 140 - 24;
const TEXT_MAX_WIDTH = 992;

const SMART_REVIEW_HELP_URL =
  'https://academy.withluminary.com/articles/5709395956-smartreview';

const VALUE_PROPS: OnboardingValueProp[] = [
  {
    Icon: ZapFastIcon,
    title: 'Faster processing',
    description: 'Get results in minutes with limited manual review required',
  },
  {
    Icon: MagicWand01Icon,
    title: 'Automated deliverables',
    description: 'Deliverables created with fewer clicks',
  },
  {
    Icon: CheckCircleBrokenIcon,
    title: 'Streamlined data review',
    description: 'Automatically approve data suggestions',
  },
];

/**
 * Body for the "SmartReview" step: a title + explanation, the Enable SmartReview
 * toggle card, the value-prop cards, and a "Learn more" link.
 */
export function SmartReviewOptionBody() {
  const { isSmartReviewEnabled, setIsSmartReviewEnabled } =
    useSmartReviewStepContext();

  return (
    <Stack
      spacing={3}
      alignItems="center"
      justifyContent="flex-start"
      sx={{ px: `${CONTENT_INSET_PX}px`, minHeight: '100%' }}
    >
      <Stack spacing={1} width="100%" maxWidth={TEXT_MAX_WIDTH}>
        <Typography variant="title-l" textAlign="center">
          Automatically create this profile with SmartReview
        </Typography>
        {/* 20px regular / 32px line-height per Figma. No design-system variant
            matches, so base on title-l (correct 20px size + text color) and
            override to regular weight and line-height. */}
        <Typography
          variant="title-l"
          textAlign="center"
          sx={{ fontWeight: FONT_WEIGHTS.regular, lineHeight: '2rem' }}
        >
          SmartReview uses AI to automatically approve all extracted data above
          a 95% confidence threshold. Lower confidence suggestions will be
          routed to you for review.
        </Typography>
      </Stack>

      <EnableSmartReviewCard
        isEnabled={isSmartReviewEnabled}
        onChange={setIsSmartReviewEnabled}
      />

      <OnboardingValuePropCards valueProps={VALUE_PROPS} />

      <Stack
        component="a"
        href={SMART_REVIEW_HELP_URL}
        target="_blank"
        rel="noreferrer"
        direction="row"
        spacing={0.5}
        alignItems="center"
        sx={{ textDecoration: 'none', color: COLORS.TEXT.SECONDARY }}
      >
        <Typography variant="body-xs">Learn more about SmartReview</Typography>
        <LinkExternal01Icon size={16} />
      </Stack>
    </Stack>
  );
}
